import React, { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import CardActionArea from '@mui/material/CardActionArea';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import BorderColor from '@mui/icons-material/BorderColor';
import {
  CircularProgress,
  FormGroup,
  IconButton,
  Tooltip,
} from '@mui/material';

import {
  CloseOutlined,
  FileDownloadOutlined,
  FileUploadOutlined,
} from '@mui/icons-material';

import axios from 'axios';
import useSession from '../../hooks/useSession';
import { useSessionContext } from '../../contexts/SessionContext';
import { IImage } from '../../global/interfaces';
import { StyledImageOverlay } from '../../pages/VirtualSession/styles';
import { StyledSideTab, StyledSideTabs } from '../StyledTabs';
import { useImageStyles } from './styles';
import { useBookingsContext } from '../../contexts/BookingsContext';
import CustomModal from '../CustomModal';
import useImageUpload from '../../hooks/useImageUpload';
import SaveIcon from '../../icons/SaveIcon';
import toggleToast from '../../utils/messaging';

interface imagesProps {
  viewTitle: string;
  sendPayload?: (props: any) => void;
}

const Images = ({ viewTitle, sendPayload }: imagesProps) => {
  const styles = useImageStyles();
  const scrollableElRef = useRef<any>();
  const bottomElRef = useRef<any>();

  const {
    selectedLocalFile,
    imageUrl,
    fileInputRef,
    imageRef,
    dimensions,
    handleFileChange,
    setSelectedLocalFile,
    handleUpload,
    isUploading,
  } = useImageUpload();

  const { uploadImage } = useSession();
  const { selectedBooking } = useBookingsContext();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [startIndex, setStartIndex] = useState<number>(0);
  const downloadCountRef = useRef(0);
  const [unselectedImages, setUnselectedImages] = useState<
    Record<string, boolean>
  >({});
  const { client, images, imagesMapRef, setSelectedImage, appendImage } =
    useSessionContext();
  const [value, setValue] = useState(0);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue || 0);
  };

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    image: IImage,
  ) => {
    console.log('::::handleDragStart from images: ', e, image);
    if (!image?.id) return;

    e.dataTransfer.setData('text/plain', image.id);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleCloseDeleteModal = () => setIsDeleteModalOpen(false);

  const handleDeleteImage = async () => {
    try {
      //const data = await deleteImage.mutateAsync({
      //  bookingId: booking?.booking_id,
      //  imageId: selectedImage?.id,
      //});

      console.log(':::data after delete image: ');
      //sendAppMessage({ 'updated-images': true }, '');
      handleCloseDeleteModal();
    } catch (error) {
      console.log(error);
    }
  };

  const imageCount = images?.length || 0;

  const convertWebpToJpg = (blob: Blob): Promise<Blob> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);

        canvas.toBlob(
          (jpgBlob) => {
            resolve(jpgBlob as Blob);
          },
          'image/jpeg',
          0.95, // Quality
        );
      };

      img.src = URL.createObjectURL(blob);
    });
  };

  const handleDownloadImages = (
    nidx: number,
    convertToJpg: boolean = false,
  ) => {
    if (!imagesMapRef.current?.has(images[nidx]) || nidx >= imageCount) return;

    let image: IImage | undefined;
    let idx = nidx;

    while (true) {
      image = imagesMapRef.current.get(images[idx]) as IImage;
      if (image?.id && unselectedImages[image.id]) {
        console.log(':::skip image: ', idx, image.id);
        idx += 1;
        continue;
      }
      break;
    }

    if (!image) return;

    downloadCountRef.current += 1;
    console.log(
      ':::handleDownloadImages',
      idx,
      image.id,
      downloadCountRef.current,
    );
    downloadImageAsync(image.src, idx, convertToJpg);
  };

  const downloadImageAsync = async (
    url: string,
    nidx: number,
    convertToJpg: boolean,
  ) => {
    const fname = convertToJpg
      ? //? `vhc_${url.split('?ts=')[0].split('/').slice(-2).join('_')}`.replace(
        //    /\.(webp|png|gif)$/,
        //  '.jpg',
        //)
        `${selectedBooking.title.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().getFullYear()}_${downloadCountRef.current}`
      : `vhc_${url.split('?ts=')[0].split('/').slice(-2).join('_')}`;

    console.log(`fetch: ${url} as ${fname}`);
    //return handleDownloadImages(nidx + 1, convertToJpg);

    const response = await axios({
      url: url,
      method: 'GET',
      responseType: 'blob',
    });

    let blob = new Blob([response.data]);

    if (convertToJpg && blob.type !== 'image/jpeg') {
      console.log('Converting to jpg...');
      blob = await convertWebpToJpg(blob);
    }

    console.log(`blob size: ${blob.size}`);
    console.log(':::url: ', url);

    const imageUrl =
      window.URL && window.URL.createObjectURL
        ? window.URL.createObjectURL(blob)
        : window.webkitURL.createObjectURL(blob);
    const link = document.createElement('a');

    link.style.display = 'none';
    link.href = imageUrl;
    link.setAttribute('download', fname);

    setTimeout(() => {
      console.log('Download completed');

      document.body.removeChild(link);
      window.URL.revokeObjectURL(imageUrl);
      console.log(`done: ${fname}`);

      handleDownloadImages(nidx + 1, convertToJpg);
    }, 250);

    document.body.appendChild(link);
    link.click();
  };

  return (
    <>
      <CustomModal
        message="Are you sure you want to delete this image?"
        primaryText="Delete image"
        handlePrimary={handleDeleteImage}
        secondaryText="Do not delete"
        handleSecondary={handleCloseDeleteModal}
        isModalOpen={isDeleteModalOpen}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple={false}
        onChange={handleFileChange}
        onClick={(e) => e.stopPropagation()}
        style={{ display: 'none' }}
      />
      <StyledSideTabs value={value} onChange={handleChange}>
        <StyledSideTab value={0} label={viewTitle} />

        <FormGroup row sx={styles.iconTray}>
          {selectedLocalFile && (
            <>
              <Tooltip title={'Upload Image'}>
                <IconButton
                  key="upload-image"
                  disabled={isUploading}
                  sx={{
                    ...styles.trayButton,
                    marginLeft: 'auto',
                  }}
                  onClick={() =>
                    handleUpload({
                      selectedBooking,
                      client,
                      uploadImage,
                      appendImage,
                    })
                  }>
                  {isUploading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <SaveIcon strokeWidth={2} sx={{ fontSize: '1.5rem' }} />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title={'Cancel'}>
                <IconButton
                  key="menu-cancel-upload"
                  sx={styles.trayButton}
                  onClick={() => {
                    setSelectedLocalFile(null);
                    //setIsImageLoading(false);
                  }}>
                  <CloseOutlined />
                </IconButton>
              </Tooltip>
            </>
          )}
          {!selectedLocalFile && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: 'auto',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    pr: '.75rem',
                  }}>
                  <Typography
                    component="label"
                    htmlFor="start-index"
                    sx={{
                      fontSize: '0.75rem',
                      position: 'absolute',
                      top: '-.75rem',
                      left: '.25rem',
                      padding: '0 4px',
                      backgroundColor: '#fff',
                      transition: 'all 0.2s',
                      opacity: startIndex > 0 ? 1 : 0,
                    }}>
                    Start Index
                  </Typography>
                  <input
                    id="start-index"
                    type="text"
                    placeholder="Start Index"
                    defaultValue="0"
                    style={{
                      width: '100px',
                      padding: '.5rem',
                      borderRadius: '.25rem',
                      border: '1px solid #ccc',
                    }}
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setStartIndex(parseInt(value) || 0);
                    }}
                  />
                </Box>
                <Tooltip title={'Download Selected Images'}>
                  <IconButton
                    onClick={() => {
                      downloadCountRef.current = startIndex - 1;
                      handleDownloadImages(0, true);
                    }}
                    disabled={!!selectedLocalFile}
                    sx={styles.trayButton}>
                    <FileDownloadOutlined sx={{ fontSize: '1.5rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Tooltip title={'Upload Images'}>
                <IconButton
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!!selectedLocalFile}
                  sx={styles.trayButton}>
                  <FileUploadOutlined sx={{ fontSize: '1.5rem' }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </FormGroup>
      </StyledSideTabs>
      {value === 0 && (
        <Box
          sx={{
            height: '100%',
            backgroundColor: '#fff',
          }}>
          {selectedLocalFile && (
            <Box sx={styles.imagePreview}>
              <img
                ref={imageRef}
                src={imageUrl}
                alt="Preview"
                style={styles.imgPreview as React.CSSProperties}
              />
              {dimensions && (
                <Box sx={styles.dimensions}>
                  {dimensions.width} × {dimensions.height}
                </Box>
              )}
              ,
            </Box>
          )}

          {!selectedLocalFile && images.length > 0 && (
            <Grid
              ref={scrollableElRef}
              sx={styles.imagesPanel}
              container
              spacing={1}>
              {images?.map((image_id: string, index: number) => {
                if (!imagesMapRef?.current?.has(image_id)) return;

                const image = imagesMapRef.current.get(image_id) as IImage;
                const {
                  id,
                  thumb: { src },
                } = image;
                const ratio =
                  image.width && image.height ? image.width / image.height : 1;

                if (!id) return null;

                return (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    key={id}
                    sx={{
                      backgroundColor: 'white !important',
                      minWidth: '18rem',
                      maxWidth: '18rem',
                      width: '18rem',
                    }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        position: 'relative',
                      }}>
                      <Checkbox
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          zIndex: 1,
                        }}
                        checked={unselectedImages[id] ? false : true}
                        onChange={(e) => {
                          setUnselectedImages({
                            ...unselectedImages,
                            [id]: unselectedImages[id] ? false : true,
                          });
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <CardActionArea
                        sx={styles.savedScreenshotWrapper}
                        onClick={() => image && setSelectedImage(image)}>
                        <img
                          draggable
                          onDragStart={(e) => handleDragStart(e, image)}
                          src={src}
                          alt={`Sessions screenshot ${index + 1}`}
                          style={{
                            ...(styles.savedScreenshot as React.CSSProperties),
                            width: ratio < 1 ? `${ratio * 90}%` : '90%',
                            height: ratio < 1 ? '90%' : `${ratio * 90}%`,
                            objectFit: 'cover',
                          }}
                        />
                        {image.description && (
                          <Box sx={styles.imageDescription} />
                        )}

                        <StyledImageOverlay sx={styles.imageOverlay}>
                          <BorderColor />
                          <Typography>Edit</Typography>
                        </StyledImageOverlay>
                      </CardActionArea>
                    </Box>
                  </Grid>
                );
              })}

              <div ref={bottomElRef}></div>
            </Grid>
          )}
        </Box>
      )}
      {value === 1 && (
        <Box>
          <Grid
            ref={scrollableElRef}
            sx={styles.imagesPanel}
            container
            spacing={1}>
            <div ref={bottomElRef}></div>
          </Grid>
        </Box>
      )}
    </>
  );
};

export default Images;
